import React, { ReactElement, useEffect, useState } from 'react';
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js';

import { createBrowserHistory } from 'history';
import useSelector from '../redux/typedHooks';

interface Props {
  children?: ReactElement | null;
}

/**
 * Provides the App Insights React plugin, and initialises the browser SDK after mount.
 *
 * The SDK is imported dynamically. It was previously a static import, so it sat in the entry's
 * static closure — downloaded, parsed and evaluated on every page load whatever the route, even
 * though nothing touches it until the effect below runs.
 *
 * This is a partial win, and the measured split is worth stating rather than rounding up. Of the
 * ~162 KB of App Insights in the production client build, **77 KB moved into lazy chunks** (web,
 * channel, core, dependencies, properties, cfgsync) and **85 KB is still in the entry closure**:
 * `analytics-js`, reached through `ReactPlugin`, which has to stay static because the context value
 * must exist at first render. Removing that last 85 KB would mean rendering children without the
 * provider until the plugin loads, which changes the context contract for every consumer — not
 * obviously worth it for 85 KB.
 *
 * The type-only import of `ApplicationInsights` is erased at compile time and creates no module
 * edge — a value import of a type is exactly how the video player ended up on every page (see
 * useCloudinaryWithVideoPlayer).
 *
 * Splitting the SDK into its own async chunk has a real cost worth naming: there is a short window,
 * from hydration until that chunk resolves and the SDK core exists, in which browser exceptions and
 * metrics have nowhere to land and are dropped. To keep the window as small as we safely can, the
 * import is kicked off in the `useState` initialiser below — on the provider's first render, a
 * commit earlier than the effect that consumes it, rather than only when a post-mount effect starts
 * to run. It stays client-only (guarded on `window`); server-side telemetry uses a separate path and
 * is unaffected by any of this.
 */
export const AppInsightsContextProvider = ({ children }: Props) => {
  const [appInsights, setAppInsights] = useState<ApplicationInsights | undefined>(undefined);
  const [isAppInsightsLoaded, setIsAppInsightsLoaded] = useState(false);
  const [reactPlugin] = useState(() => new ReactPlugin());

  // Start fetching the browser SDK chunk on first render, client-side only. Running this in the state
  // initialiser (not an effect) means the request is already in flight while React finishes hydrating.
  const [appInsightsSdkPromise] = useState(() =>
    typeof window !== 'undefined' ? import('@microsoft/applicationinsights-web') : undefined,
  );

  const applicationInsightsInstrumentationKey = useSelector((state) => state.application.appInsightsInstrumentationKey);

  useEffect(() => {
    if (!reactPlugin || appInsights != null || !appInsightsSdkPromise) return undefined;

    let cancelled = false;
    const browserHistory = createBrowserHistory();

    appInsightsSdkPromise
      .then(({ ApplicationInsights: AppInsightsSdk }) => {
        if (cancelled) return;
        setAppInsights(
          new AppInsightsSdk({
            config: {
              instrumentationKey: applicationInsightsInstrumentationKey,
              extensions: [reactPlugin],
              disablePageUnloadEvents: ['unload'],
              extensionConfig: {
                [reactPlugin.identifier]: { history: browserHistory },
              },
            },
          }),
        );
      })
      .catch(() => {
        // Telemetry must never break the page. A failed load simply means no browser telemetry.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactPlugin]);

  useEffect(() => {
    if (typeof appInsights !== 'undefined' && appInsights !== null && !isAppInsightsLoaded) {
      appInsights.loadAppInsights();
      appInsights.trackPageViewPerformance({ name: 'First render' });
      setIsAppInsightsLoaded(true);
    }
  }, [appInsights, isAppInsightsLoaded]);

  return <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>;
};
